/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-23 19:58:17
 *
 *******************************************************/
//@flow
import _ from 'lodash';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import { Card, Button, SkeletonDisplayText } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import NoMergeValues from 'src/components/views/merge-values/item/no-merge-values';
import TableRow from 'src/components/views/merge-values/item/table-row';
import ConfirmRemoveModal from 'src/components/views/merge-values/confirm-remove-modal';
import ConfirmTypeModal from 'src/components/views/merge-values/confirm-type-modal';
import RuleModal from './rule-modal';

import * as MergeValuesActions from 'actions/merge-values-actions';

import type { MergeValuesRulesByTypeType } from 'src/models/merge-values';

type Props = { type: string, loading: boolean, rules: MergeValuesRulesByTypeType };

type ComposedProps = Props & WithI18nProps;

type State = {
  activeModal: boolean,
  activeModalRemove: boolean,
  activeConfirmTypeModal: boolean,
  index: number
};

class Rules extends Component<ComposedProps, State> {
  state = {
    activeModal: false,
    activeModalRemove: false,
    activeConfirmTypeModal: false,
    index: -1
  };

  render() {
    const { rules, type, i18n, loading } = this.props;
    const isEmptyRules = _.isEmpty(rules);
    const { activeModal, activeModalRemove, index, activeConfirmTypeModal } = this.state;

    return (
      <Card
        title={i18n.translate('AdminHTML.MergeValues.Item.mergedValues')}
        actions={
          !isEmptyRules
            ? [
                {
                  content: i18n.translate('AdminHTML.MergeValues.MergeValues.addNewPlus'),
                  onAction: this.handleAddRule
                }
              ]
            : []
        }>
        <Card.Section>
          <div className="Polaris-DataTable BC-DataTable__MergeValues">
            <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
              <table className="Polaris-DataTable__Table">
                <thead>
                  <tr>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.MergeValues.Item.mergedValues_Status')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.MergeValues.Item.mergedValues_MergeValues')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.MergeValues.Item.mergedValues_CurrentValues')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--numeric">
                      {i18n.translate('AdminHTML.MergeValues.Item.mergedValues_Actions')}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    [...Array(3).keys()].map((item, key) => (
                      <tr key={key}>
                        {[...Array(4).keys()].map((item, key) => (
                          <td className="Polaris-DataTable__Cell" key={key}>
                            <SkeletonDisplayText size="small" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : !isEmptyRules ? (
                    rules.map((item, key) => {
                      return (
                        <TableRow
                          item={item}
                          index={key}
                          key={key}
                          updateStatus={this.updateStatus}
                          handleEditRule={this.handleEditRule}
                          updateSelected={this.updateSelected}
                          toggleModal={this.toggleModal}
                        />
                      );
                    })
                  ) : (
                    <tr />
                  )}
                </tbody>
              </table>
              {isEmptyRules && !loading ? <NoMergeValues handleAdd={this.handleAddRule} /> : ''}
            </div>
          </div>
        </Card.Section>
        {!isEmptyRules ? (
          <Card.Section>
            <div style={{ textAlign: 'right' }}>
              <Button plain onClick={this.handleAddRule}>
                {i18n.translate('AdminHTML.MergeValues.MergeValues.addNewPlus')}
              </Button>
            </div>
          </Card.Section>
        ) : (
          ''
        )}
        <RuleModal
          active={activeModal}
          handleChange={() => this.toggleModal('activeModal')}
          index={index}
          rules={rules}
          type={type}
        />
        <ConfirmRemoveModal
          active={activeModalRemove}
          handleChange={() => this.toggleModal('activeModalRemove')}
          handleRemove={this.deleteMergeValuesRule}
          title={i18n.translate('AdminHTML.MergeValues.Item.confirmRemoveModal_Title')}
          content={i18n.translate('AdminHTML.MergeValues.Item.confirmRemoveModal_Content')}
        />
        <ConfirmTypeModal
          active={activeConfirmTypeModal}
          handleChange={() => this.toggleModal('activeConfirmTypeModal')}
        />
      </Card>
    );
  }

  // Handle click delete button
  deleteMergeValuesRule = async () => {
    const { index } = this.state;
    const { deleteMergeValuesRule, rules, type } = this.props;
    const { id } = rules[index];

    await deleteMergeValuesRule(type, id);
    this.toggleModal('activeModalRemove');
  };

  // Handle click status
  updateStatus = (e: Object, id: string) => {
    const { updateStatus, type } = this.props;
    updateStatus(type, id, e.target.checked ? 'active' : 'disabled');
  };

  // Handle add option button
  handleAddRule = () => {
    const { type } = this.props;
    if (_.isEmpty(type)) {
      this.toggleModal('activeConfirmTypeModal');
    } else {
      this.setState({ index: -1 });
      this.toggleModal('activeModal');
    }
  };

  // Handle edit option button
  handleEditRule = id => {
    this.updateSelected(id);
    this.toggleModal('activeModal');
  };

  // Update selected id
  updateSelected = id => {
    this.setState({ index: id });
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  deleteMergeValuesRule: (type: string, id: string) =>
    dispatch(MergeValuesActions.deleteMergeValuesRule(type, id, ownProps.i18n)),
  updateStatus: (type, id, status) =>
    dispatch(MergeValuesActions.updateMergeValuesRuleStatus(type, id, status, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  return {};
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Rules));
